import {
  useContext, useMemo, useState,
} from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import UndoIcon from '@mui/icons-material/Undo';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  FETCH_PORTFOLIO_REPORTS,
} from '../../../4-module/widgets/portfolio/components/queries';
import { UserContext } from '../../../../providers/userContextProvider';
import { OvColumn, PortfolioReports } from '../rebalancePage/sharedTypes';
import { PortfolioSecurity, PortfolioSubAccount } from '../../../4-module/widgets/portfolio/components/interfaces';
import { HoldingReport, PortfolioReport } from '../../../../interfaces';
import {
  buildPortfolioSubAccount, buildSecurity,
  sortPortfolioReportSubAccounts, sortSecurities,
} from '../../../4-module/widgets/portfolio/components/utils';
import { SubAccountReport } from '../../../../interfaces/subAccountReport';
import { Divider } from '../../../2-component/divider/divider';
import {
  Button, Card, CardContent, Menu, MenuItem, Checkbox,
} from '../../../2-component';
import { Grid, Typography } from '../../../1-primative';
import { formatMoneyValue } from '../../../../util';
import OvTable from '../OvTable/OvTable';
import {
  SecurityColumn, CurrentCashColumn, CurrentPercentColumn, CurrentHashColumn, TargetCashColumn, TargetPercentColumn, TargetHashColumn, DriftCashColumn, DriftPercentColumn, DriftHashColumn,
} from './portfolioColumns';

const columns: OvColumn<PortfolioSecurity>[] = [
  {
    header: 'Security', accessor: 'security', sort: false, cell: (row: PortfolioSecurity) => <SecurityColumn data={row} />,
  }, {
    header: 'Current $', accessor: 'current_$', sort: false, cell: (row: PortfolioSecurity) => <CurrentCashColumn data={row} />,
  }, {
    header: 'Current %', accessor: 'current_%', sort: false, cell: (row: PortfolioSecurity) => <CurrentPercentColumn data={row} />,
  }, {
    header: 'Current #', accessor: 'current_#', sort: false, visible: false, cell: (row: PortfolioSecurity) => <CurrentHashColumn data={row} />,
  }, {
    header: 'Target $', accessor: 'target_$', sort: false, cell: (row: PortfolioSecurity) => <TargetCashColumn data={row} />,
  }, {
    header: 'Target %', accessor: 'target_%', sort: false, cell: (row: PortfolioSecurity) => <TargetPercentColumn data={row} />,
  }, {
    header: 'Target #', accessor: 'target_#', sort: false, visible: false, cell: (row: PortfolioSecurity) => <TargetHashColumn data={row} />,
  }, {
    header: 'Drift $', accessor: 'drift_$', sort: false, cell: (row: PortfolioSecurity) => <DriftCashColumn data={row} />,
  }, {
    header: 'Drift %', accessor: 'drift_%', sort: false, cell: (row: PortfolioSecurity) => <DriftPercentColumn data={row} />,
  }, {
    header: 'Drift #', accessor: 'drift_#', sort: false, visible: false, cell: (row: PortfolioSecurity) => <DriftHashColumn data={row} />,
  },
];

// const subAccountColumns: OvColumn<PortfolioSecurity>[] = [
//   { header: 'Current $', accessor: 'current_$', sort: false },
//   { header: 'Trade $', accessor: 'trade_$', sort: false },
//   { header: 'Post $', accessor: 'post_$', sort: false },
// ];

const PortfolioPage = () => {
  const { id } = useParams();
  const { activeOrganization } = useContext(UserContext);
  const { t } = useTranslation(['components', 'accountTypes', 'shared']);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [displayDollar, setDisplayDollar] = useState<boolean>(true);
  const [displayPercent, setDisplayPercent] = useState<boolean>(true);
  const [displayHash, setDisplayHash] = useState<boolean>(false);
  const [displayInactive, setDisplayInactive] = useState<boolean>(false);

  // const [currentPage, setCurrentPage] = useState<number>(1);
  // const [rowsPerPage, setRowsPerPage] = useState<number>(15);
  // const [totalItems, setTotalItems] = useState<number>(0);

  const currentPage = 1;
  const rowsPerPage = 15;
  const totalItems = 0;

  const [defaultColumns, setDefaultColumns] = useState<OvColumn<PortfolioSecurity>[]>(columns);

  const [portfolio, setPortfolio] = useState<PortfolioReport | null>(null);

  const toggleDefaultColumn = (accessor: string, visible: boolean): void => {
    setDefaultColumns((prev) => prev.map((column) => (column.accessor === accessor ? { ...column, visible } : column)));
  };

  const toggleAutoRebalance = (event: React.MouseEvent<HTMLButtonElement | HTMLButtonElement | HTMLLIElement>) => {
    setAnchorEl(isEmpty(anchorEl) && !isEmpty(event) ? event.currentTarget : null);
  };

  // const [updatePortfolio, { loading: loadingRefresh }] = useMutation(REFRESH_PORTFOLIO_REPORT, {
  //   fetchPolicy: 'no-cache',
  //   notifyOnNetworkStatusChange: true,
  //   onCompleted: (response: PortfolioReports<PortfolioReport>) => {
  //     const updatedPortfolio: PortfolioReport[] = response.fetchPortfolioReports.portfolioReports;
  //     setPortfolio(updatedPortfolio.length ? updatedPortfolio[0] : null);
  //   },
  // });

  const { loading } = useQuery(FETCH_PORTFOLIO_REPORTS, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        filter: {
          subAccountId: id,
          organizationId: activeOrganization.id,
        },
      },
    },
    onCompleted: (response: PortfolioReports<PortfolioReport>) => {
      const updatedPortfolio: PortfolioReport[] = response.fetchPortfolioReports.portfolioReports;
      setPortfolio(updatedPortfolio.length ? updatedPortfolio[0] : null);
    },
  });

  const subAccounts: PortfolioSubAccount[] = useMemo(() => {
    if (isEmpty(portfolio)) { return []; }

    const subAccountList: PortfolioSubAccount[] = (portfolio?.subAccounts || [])
      .reduce((total: PortfolioSubAccount[], subAccountReport: SubAccountReport) => {
        const { subAccount } = subAccountReport;
        if (!subAccount?.id) { return total; }
        return [...total, buildPortfolioSubAccount(subAccountReport, t)];
      }, []);

    subAccountList.sort(sortPortfolioReportSubAccounts);

    return subAccountList;
  }, [portfolio, t]);

  const securities: PortfolioSecurity[] = useMemo(() => {
    if (isEmpty(portfolio) || isEmpty(subAccounts)) { return []; }

    const securityList: PortfolioSecurity[] = (portfolio?.holdings || [])
      .reduce((total: PortfolioSecurity[], holding: HoldingReport): PortfolioSecurity[] => {
        const { financialProduct } = holding;

        if (!financialProduct?.id) return total;
        if (!holding.taxRank && holding.expectedValueCents === 0 && holding.valueCents === 0) return total;

        const subAccountWithHoldings = subAccounts.map((subAccount: PortfolioSubAccount) => {
          const subAccountHolding = subAccount.holdings.find((subAccountHoldings) => subAccountHoldings.financialProduct.id === financialProduct.id);
          return { ...subAccount, holding: subAccountHolding };
        });

        return [...total, buildSecurity(holding, subAccountWithHoldings)];
      }, []);

    securityList.sort(sortSecurities);

    return [{
      id: 'CASH',
      ticker: t('portfolioTable.cash'),
      expectedValueCents: portfolio?.expectedCashCents ?? 0,
      expectedPercentage: portfolio?.expectedCashPercentage ?? 0,
      currentPriceCents: 100,
      translatedName: t('canadianCash'),
      taxRank: null,
      financialProductTaxRank: 99,
      valueCents: 0,
      subAccounts,
    }, ...securityList];
  }, [portfolio, t, subAccounts]);

  return (
    <Grid container gap={0}>
      <Grid item xs={12} mb={5}>
        <Card loading={loading}>
          <CardContent>
            <Typography variant='headingLarge' mb={4}>
              Portfolio Details
            </Typography>

            <Grid container gap={1} flexWrap='nowrap'>
              <Grid item xs={2}><Typography weight='bold' colorVariant='variant'>Account Name</Typography></Grid>
              <Grid item xs={2}><Typography weight='bold' colorVariant='variant'>Account Type</Typography></Grid>
              <Grid item xs={2}><Typography weight='bold' colorVariant='variant'>Taxability</Typography></Grid>
              <Grid item xs={2}><Typography weight='bold' colorVariant='variant'>Value</Typography></Grid>
              <Grid item xs={2}><Typography weight='bold' colorVariant='variant'>Settled Cash</Typography></Grid>
              <Grid item xs={2}><Typography weight='bold' colorVariant='variant'>Expected Cash</Typography></Grid>
            </Grid>
          </CardContent>
          { subAccounts.length > 0 && subAccounts.map((subAccount: PortfolioSubAccount) => (
            <>
              <Divider variant='fullWidth' />
              <CardContent>
                <Grid container gap={2} flexWrap='nowrap'>
                  <Grid item xs={2}>
                    <Typography>{subAccount.name}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography>{subAccount.holding?.financialProduct?.name}</Typography>
                  </Grid>
                  <Grid item xs={2} justifySelf='center'>
                    <Typography>-</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography>{subAccount.type}</Typography>
                  </Grid>
                  <Grid item xs={2} justifySelf=''>
                    <Typography>{formatMoneyValue(subAccount.cashCents)}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography>{formatMoneyValue(subAccount.expectedCashCents)}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </>
          )) }
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card loading={loading}>
          <CardContent>

            <Grid container gap={0} justifyContent='space-between' alignItems='center'>
              <Grid item xs='auto'>
                <Grid container gap={2}>
                  <Grid item xs='auto'>
                    <Checkbox
                      checked={displayDollar}
                      onChange={() => {
                        setDisplayDollar((prev) => {
                          toggleDefaultColumn('current_$', !prev);
                          toggleDefaultColumn('target_$', !prev);
                          toggleDefaultColumn('drift_$', !prev);
                          return !prev;
                        });
                      }}
                      label='Display $'
                    />
                  </Grid>
                  <Grid item xs='auto'>
                    <Checkbox
                      checked={displayPercent}
                      onChange={() => {
                        setDisplayPercent((prev) => {
                          toggleDefaultColumn('current_%', !prev);
                          toggleDefaultColumn('target_%', !prev);
                          toggleDefaultColumn('drift_%', !prev);
                          return !prev;
                        });
                      }}
                      label='Display %'
                    />
                  </Grid>
                  <Grid item xs='auto'>
                    <Checkbox
                      checked={displayHash}
                      onChange={() => {
                        setDisplayHash((prev) => {
                          toggleDefaultColumn('current_#', !prev);
                          toggleDefaultColumn('target_#', !prev);
                          toggleDefaultColumn('drift_#', !prev);
                          return !prev;
                        });
                      }}
                      label='Display #'
                    />
                  </Grid>
                  <Grid item xs='auto'>
                    <Checkbox
                      checked={displayInactive}
                      onChange={() => setDisplayInactive((prev) => !prev)}
                      label='Display Inactive Accounts'
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs='auto'>
                <Grid container gap={2}>
                  <Grid item xs='auto'>
                    <Button
                      leadingIcon={UndoIcon}
                      variant='outlined'
                      onClick={() => {}}
                      label={t('revert')}
                    />
                  </Grid>
                  <Grid item xs='auto'>
                    <Button
                      label={t('autoRebalance')}
                      trailingIcon={ArrowDropDownIcon}
                      variant='outlined'
                      aria-controls={open ? 'basic-menu' : undefined}
                      aria-haspopup='true'
                      aria-expanded={open ? 'true' : undefined}
                      onClick={toggleAutoRebalance}
                    />

                    <Menu anchorEl={anchorEl} open={open} onClose={toggleAutoRebalance} disableAutoFocus={true} disableAutoFocusItem={true}>
                      <MenuItem
                        onClick={toggleAutoRebalance}>
                        {t('optimizer')}
                      </MenuItem>

                      {subAccounts.length === 1 && (
                        <MenuItem
                          onClick={toggleAutoRebalance}>
                          {t('autoFillToTarget')}
                        </MenuItem>
                      )}
                    </Menu>
                  </Grid>
                  <Grid item xs='auto'>
                    <Button
                      label='Execute Trade'
                      variant='filled'
                      onClick={() => {}}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>

          <Divider variant='fullWidth' />

          <Grid container gap={0}>
            <Grid item xs={12} mt={3}>
              <OvTable<PortfolioSecurity>
                data={securities} columns={defaultColumns} loading={loading} checkbox={false} pagination={false}
                linesPerPage={rowsPerPage} initialPage={currentPage} totalLines={totalItems} />
            </Grid>
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
};

export default PortfolioPage;
